// Which status badge a catalog item wears, and which EI tiers it names.
//
// Pure and shape-local: `lib/` sits below both graphs and must never import
// upward to reach a wire type (`client/docs/architecture.md`, "The dependency
// rule"), so the flags this reads are declared here. That the item wire face
// actually carries them is pinned in `@/api/contracts/catalog-wire-drift`, the
// one tier that can see both sides.
//
// **The three views do not draw the same badges, and that asymmetry is the
// donor's, transcribed rather than tidied up** (owner ruling, 2026-08-26:
// parity is the north star until an exception is granted). In modsy:
//
//   - the **table** prints `ItemStatusLabel` — a plain chip, sentence case, and
//     no EI at all (`items-data-table.tsx`, the untitled `is_core` column);
//   - the **row** prints a corner badge/chip, title case, and no EI
//     (`item-data-row.tsx`, which does not import `EITiers`);
//   - the **card** prints that same corner badge/chip *plus* one chip per EI
//     tier (`item-card.tsx`).
//
// Hence `variant`: the label text itself differs between the table's chip and
// the card's, and hence EI being its own function rather than a fourth badge
// kind — on the card the tiers are drawn somewhere else entirely, under the
// image rather than over the corner.
//
// The rules, transcribed (`ItemStatusLabel`, `shouldShowCoreItemBadge`):
//
//   - Not Rec.   `isNotRecommended`
//   - Pending    `!isApproved`      (reached only when not not-recommended)
//   - Core       `isCore`           (reached only when approved and not not-rec.)
//   - EI tiers   `isApproved`, one chip per tier — card view only
//
// **At most one status badge, always.** The donor's early-return chain is what
// makes that true, and its order encodes which signal wins: an item flagged
// against the business does not also get to advertise that it is core, and an
// item still awaiting approval does not advertise it either.

/** One EI tier, as a badge names it. */
export interface CatalogEiTier {
  id: number;
  name: string;
}

/**
 * The fields the **details page's** chain reads — and only those.
 *
 * Split out of `CatalogItemBadgeInput` below because the two chains genuinely
 * ask different questions: `itemDetailStatusBadge` and `itemShowsEiMark` never
 * look at `isNotRecommended` (`Badges.tsx:10-11` has no such arm), and the
 * anonymous product page's payload does not carry it — the public wire omits
 * it deliberately, where "what is absent is the contract" (#308).
 *
 * Requiring the field anyway would force that page to pass a `false` it was
 * never told, which is a view asserting a fact about the item on its payload's
 * behalf. Narrowing the parameter says the true thing instead: this rule does
 * not consult that flag, so a caller need not have one.
 */
export interface CatalogDetailBadgeInput {
  isCore: boolean;
  isApproved: boolean;
  /**
   * The item's *alive* EI tiers, named and in display order — never re-derived
   * here, and never filtered by name (ADR-0005 decision 4: EI-ness is the
   * tier's own column, so renaming a tier cannot change it). The donor filtered
   * client-side on an `EI` name prefix; this side is handed the answer.
   *
   * Optional because the wire field is: Pydantic defaults it, so a Python that
   * predates it omits the key. Absent and empty mean the same thing here — no
   * tiers to name — which is why the rule below reads it through `?? []` rather
   * than branching.
   */
  eiTiers?: readonly CatalogEiTier[];
  /**
   * Python's own answer to "is this an EI item", derived from the item's alive
   * tier links. The details page's EI mark trusts it even when the named tiers
   * are not resolvable — see `itemShowsEiMark`.
   */
  hasEiTiers?: boolean;
}

/**
 * The fields the **list's** chain reads — the details page's, plus the one
 * signal only that chain consults. Every catalog item summary carries them.
 */
export interface CatalogItemBadgeInput extends CatalogDetailBadgeInput {
  isNotRecommended: boolean;
}

export type CatalogStatusBadgeKind = "core" | "not-recommended" | "pending";

/**
 * Which of the donor's two badge components is being drawn.
 *
 * `"chip"` is the card and row's overlay; `"label"` is the table cell's. They
 * are separate components in modsy and they disagree on capitalisation, so the
 * text is picked here rather than being written per view — a view spelling its
 * own label would be one rename away from disagreeing with the other two.
 */
export type CatalogBadgeVariant = "chip" | "label";

export interface CatalogStatusBadge {
  kind: CatalogStatusBadgeKind;
  label: string;
}

const STATUS_LABELS: Record<CatalogStatusBadgeKind, Record<CatalogBadgeVariant, string>> = {
  core: { chip: "Core Item", label: "Core item" },
  "not-recommended": { chip: "Not Recommended", label: "Not Recommended" },
  pending: { chip: "Pending Approval", label: "Pending approval" },
};

/** The one badge this item has earned, or none. */
function statusBadgeKind(item: CatalogItemBadgeInput): CatalogStatusBadgeKind | null {
  if (item.isNotRecommended) return "not-recommended";
  if (!item.isApproved) return "pending";
  if (item.isCore) return "core";
  return null;
}

/**
 * The item's status badge in the requested variant, or `null` for an ordinary
 * approved non-core item — which is most of the catalog.
 */
export function itemStatusBadge(
  item: CatalogItemBadgeInput,
  variant: CatalogBadgeVariant,
): CatalogStatusBadge | null {
  const kind = statusBadgeKind(item);
  return kind === null ? null : { kind, label: STATUS_LABELS[kind][variant] };
}

/**
 * The EI tiers to name, in display order — **the card view's alone.**
 *
 * Empty while the item is unapproved, however its tier links happen to be set:
 * EI-ness is a claim the business has accepted, and an item still in review must
 * not advertise it. That is a disclosure decision, not styling.
 */
export function itemEiTiers(item: CatalogDetailBadgeInput): readonly CatalogEiTier[] {
  if (!item.isApproved) return [];
  return item.eiTiers ?? [];
}

/**
 * Whether the EI mark is drawn over the item's picture.
 *
 * The placements ask slightly different questions, and the difference is the
 * donor's. The card resolves tier ids to names and shows the mark when there are
 * any (`item-card.tsx`); the details page checks the derived flag *first* and
 * falls back to the ids (`Lightbox/Images.tsx:12-19`), so an approved EI item
 * whose tier names cannot be resolved still carries the mark there. Both are
 * gated on approval before anything else.
 *
 * `"preview"` — the Add Variant dialog's left-column card — asks the **card's**
 * question, not the details page's, even though it sits on the details surface:
 * it resolves the tier names itself and gates on there being any
 * (`AddVariantIsland/components/item-details.tsx:56-62`). Only the placement is
 * its own. So does `"island"` — the Item Search island's card (#798), whose
 * `showEITiersBadge` is `isApproved && variantEITiers.length > 0`.
 */
export function itemShowsEiMark(
  item: CatalogDetailBadgeInput,
  placement: "card" | "detail" | "preview" | "island",
): boolean {
  if (!item.isApproved) return false;
  if (placement === "detail" && item.hasEiTiers) return true;
  return itemEiTiers(item).length > 0;
}

/**
 * The **details page's** status badge — a different chain from the list's.
 *
 * `Badges.tsx:10-11` asks two questions and only two: unapproved wins, then
 * core. There is no Not Recommended arm, so an item flagged against the
 * business still shows Core Item on its own page while its card in the list
 * shows Not Recommended.
 *
 * That is the donor's inconsistency, transcribed rather than reconciled: the
 * two screens genuinely disagree, and picking one of them would be this port
 * deciding what a modsy user sees. It is a separate function rather than a
 * third `CatalogBadgeVariant` because what differs is the *rule*, not the
 * wording — a variant would have to reach inside `statusBadgeKind` and give it
 * a mode, which is how one chain becomes two chains sharing a body.
 *
 * The labels are the chip variant's, which is what the donor's PDP prints.
 */
export function itemDetailStatusBadge(item: CatalogDetailBadgeInput): CatalogStatusBadge | null {
  if (!item.isApproved) return { kind: "pending", label: STATUS_LABELS.pending.chip };
  if (item.isCore) return { kind: "core", label: STATUS_LABELS.core.chip };
  return null;
}
